/**
 * The browser half of a Covering Fire split move (`landing-zone-full-automation` Q5, 5.2). RAW, Core
 * p.97: the shot may land "at any point during their movement (e.g., waiting until they exit cover)".
 *
 * The engine's offer lists each path cell after the start as an `at:x,y` option
 * (`SPLIT_OPTION_PREFIX`, `checkpointRegistry.ts`), in path order. The prompt window shows those as
 * ONE "Pick where along the path" button ({@link promptButtonOptions}); picking it puts the board in
 * a cell pick over exactly those cells ({@link SplitPick}), each marked by the mover's cover from the
 * shooter ({@link splitPickSightlines}), and a click on one answers with its option id. The cells
 * come off the options, never a client-side route, so the pick can't offer a cell the server won't
 * accept.
 */
import { squareCell, worldFor, type Combatant, type EncounterState, type SquareCell } from "@lancer-vtt/domain";
import { SPLIT_OPTION_PREFIX, moveCheckpointWatcherId, checkpointMoverId, type LoadedOverlay } from "@lancer-vtt/rules";
import type { PromptOption, PromptRequest } from "@lancer-vtt/shared";
import { armedCellSightlines, type CellSightline } from "./combatInspector";
import type { TargetingBanner } from "./TargetingMode";

/** The option id the prompt window's "Pick where along the path" button stands for. Never sent: it opens the board pick. */
export const PICK_ALONG_PATH_OPTION = "pick_along_path";

/** Is `optionId` a split-cell answer (`at:x,y`)? */
export function isSplitOption(optionId: string): boolean {
  return optionId.startsWith(SPLIT_OPTION_PREFIX);
}

/** The square cell a split-cell option names, or `null` for any other option. The combat board is square. */
export function splitOptionCell(optionId: string): SquareCell | null {
  if (!isSplitOption(optionId)) return null;
  const m = /^(-?\d+),(-?\d+)$/.exec(optionId.slice(SPLIT_OPTION_PREFIX.length));
  return m ? squareCell(Number(m[1]), Number(m[2])) : null;
}

/** The buttons a prompt shows: its options with the split cells folded into one {@link PICK_ALONG_PATH_OPTION}. */
export function promptButtonOptions(options: readonly PromptOption[]): PromptOption[] {
  const plain = options.filter((o) => !isSplitOption(o.id));
  return plain.length === options.length ? [...options] : [...plain, { id: PICK_ALONG_PATH_OPTION, label: "Pick where along the path" }];
}

/** A board pick of the cell a Covering Fire shot lands at: the offer it answers, who shoots, who moves, and the path cells in order. */
export type SplitPick = { promptId: string; shooterId: string; moverId: string; cells: SquareCell[] };

/** The {@link SplitPick} a Covering Fire offer opens, or `null` for a prompt with no split cells. */
export function splitPickFor(prompt: PromptRequest): SplitPick | null {
  const shooterId = moveCheckpointWatcherId(prompt.promptId);
  const moverId = checkpointMoverId(prompt.intent);
  const cells = prompt.options.map((o) => splitOptionCell(o.id)).filter((c): c is SquareCell => c !== null);
  if (shooterId === undefined || moverId === undefined || cells.length === 0) return null;
  return { promptId: prompt.promptId, shooterId, moverId, cells };
}

/** The option id that answers `pick` with `cell`, or `null` when `cell` is not on the path. */
export function splitAnswerAt(state: EncounterState, pick: SplitPick, cell: { x: number; y: number }): string | null {
  const world = worldFor(state);
  // The board hands back a bare `{x, y}`; tag it before comparing.
  const clicked = squareCell(cell.x, cell.y);
  const hit = pick.cells.find((c) => world.equals(c, clicked));
  return hit ? `${SPLIT_OPTION_PREFIX}${world.key(hit)}` : null;
}

/**
 * Each path cell as the shooter would see the mover standing there: line of sight and the cover
 * degree, through the same per-cell read an armed attack's wash uses (`armedCellSightlines`). No
 * weapon, so no Arcing/Seeking exemption: Covering Fire's shot is a Heavy ranged weapon.
 */
export function splitPickSightlines(args: { state: EncounterState; pick: SplitPick; overlay?: LoadedOverlay }): CellSightline[] {
  const { state, pick, overlay } = args;
  const shooter: Combatant | undefined = state.combatants[pick.shooterId];
  if (!shooter) return [];
  const world = worldFor(state);
  return armedCellSightlines({
    state,
    attacker: shooter,
    reachCells: pick.cells.map((cell) => ({ key: world.key(cell), cell })),
    weapon: null,
    ...(overlay !== undefined ? { overlay } : {}),
  });
}

/** The targeting banner while a split pick is armed. */
export function splitPickBanner(state: EncounterState, pick: SplitPick): TargetingBanner {
  const shooter = state.combatants[pick.shooterId]?.name ?? pick.shooterId;
  const mover = state.combatants[pick.moverId]?.name ?? pick.moverId;
  return { kind: "attack", title: `${shooter}: Covering Fire along ${mover}'s path`, sub: "Click the cell where the shot lands. Ringed cells give cover." };
}
